// @file Tiptap text direction extension selection
import type { AnyExtension } from '@tiptap/core'
import TextDirection from 'tiptap-text-direction'
import { tipTapDefaultExtensions } from 'tldraw'

/**
 * Get appropriate text direction extensions based on document direction
 *
 * For users on RTL, we need the custom
 * TextDirection extension as TLDraw does not apply text-direction to list items,
 * leading to issues like: https://linear.app/padlet/issue/PRO-22652/handle-applying-bullet-points-on-rtl-applies-the-bullet-still-on-the
 *
 * For users not on RTL, there can be some issues with non-English characters like Japanese
 * with the custom TextDirection extension: https://github.com/amirhhashemi/tiptap-text-direction/issues/18
 * In which case we load the default textDirection extension that TLDraw provides:
 * https://github.com/tldraw/tldraw/blob/v3.14.2/packages/tldraw/src/lib/utils/text/textDirection.ts
 *
 * @returns Array of extensions for RTL or LTR text handling
 */

export function whiteboardTiptapTextDirectionExtensions(direction: 'ltr' | 'rtl'): AnyExtension[] {
  return direction === 'rtl'
    ? [
        // For RTL: Use custom TextDirection extension
        TextDirection.configure({
          types: ['heading', 'paragraph', 'bulletList', 'orderedList'],
        }),
      ]
    : ([
        // For LTR: Use default textDirection extension from tldraw
        ...tipTapDefaultExtensions.filter((ext) => ext.name === 'textDirection'),
      ] as AnyExtension[])
}
